import { useEffect, useState, useCallback } from 'react';
import expenseService from '../services/expenseService';
import { savePreference } from '../utils/preferenceSync';

const STORAGE_KEY = 'omnis:displayCurrency';
const DEFAULT_CURRENCY = 'USD';
const FALLBACK_LIST = [
    'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'CNY', 'HKD',
    'SGD', 'THB', 'INR', 'BRL', 'MXN', 'SEK', 'NOK', 'DKK', 'PLN',
    'CZK', 'HUF', 'NZD', 'ZAR', 'TWD', 'AED', 'RUB',
];

/**
 * Synced display-currency preference (#480: server-persisted in the profile,
 * cached in localStorage) + the list of supported codes fetched
 * from the backend (Frankfurter/ECB + the TWD/AED/RUB extras, #490/#489). Falls back to a static list if the
 * fetch fails. Writing to the preference broadcasts a window event so other
 * components using this hook update in sync within the same tab.
 */
export const useCurrencyPreference = () => {
    const [currency, setCurrencyState] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) || DEFAULT_CURRENCY;
        } catch (_) {
            return DEFAULT_CURRENCY;
        }
    });
    const [supported, setSupported] = useState(FALLBACK_LIST);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await expenseService.listCurrencies();
                if (!cancelled && Array.isArray(res?.currencies) && res.currencies.length > 0) {
                    setSupported(res.currencies);
                }
            } catch (_) {
                // keep fallback
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const onChange = (e) => {
            if (e?.detail?.currency) setCurrencyState(e.detail.currency);
            else setCurrencyState(DEFAULT_CURRENCY);   // a reverted/cleared value (#480)
        };
        window.addEventListener('omnis:currency-changed', onChange);
        return () => window.removeEventListener('omnis:currency-changed', onChange);
    }, []);

    // #480 — the preference is SERVER-persisted (synced across devices):
    // optimistic local write + broadcast, then PATCH /auth/profile. Resolves
    // true once the server saved it (Settings shows "Saved"), false when signed
    // out; rejects (already reverted) when the save failed.
    const setCurrency = useCallback((next) => {
        const clean = (next || DEFAULT_CURRENCY).toUpperCase();
        setCurrencyState(clean);
        return savePreference('display_currency', clean);
    }, []);

    return { currency, setCurrency, supported };
};

export default useCurrencyPreference;
